import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPendingOwners, approveOwner, rejectOwner, fetchOwnerDocumentUrl } from '../../api/cafeApi';
import { PendingOwner } from '../../types/cafe';
import { TDS } from '../../constants/tds';
import { TdsButton, TdsSpinner } from '../../components/tds';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [owners, setOwners] = useState<PendingOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  // 사업자등록증 미리보기 모달 상태
  const [docOwnerId, setDocOwnerId] = useState<number | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string>('');

  const token = sessionStorage.getItem('admin_token');

  const loadOwners = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchPendingOwners(token);
      setOwners(data);
    } catch {
      setError('목록을 불러오는 데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/admin', { replace: true });
      return;
    }
    void loadOwners();
  }, [token, navigate, loadOwners]);

  const handleApprove = async (id: number) => {
    if (!token) return;
    setActionId(id);
    try {
      await approveOwner(id, token);
      setOwners((prev) => prev.filter((o) => o.id !== id));
    } catch {
      alert('승인 처리에 실패했습니다');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id: number) => {
    if (!token) return;
    setActionId(id);
    try {
      await rejectOwner(id, token);
      setOwners((prev) => prev.filter((o) => o.id !== id));
    } catch {
      alert('거절 처리에 실패했습니다');
    } finally {
      setActionId(null);
    }
  };

  // presigned URL은 5분 만료. 모달을 열 때마다 새로 발급한다.
  const handleViewDocument = async (id: number) => {
    if (!token) return;
    setDocOwnerId(id);
    setDocUrl(null);
    setDocError('');
    setDocLoading(true);
    try {
      const url = await fetchOwnerDocumentUrl(id, token);
      setDocUrl(url);
    } catch (err) {
      setDocError(err instanceof Error ? err.message : '사업자등록증을 불러올 수 없습니다');
    } finally {
      setDocLoading(false);
    }
  };

  const closeDocument = () => {
    setDocOwnerId(null);
    setDocUrl(null);
    setDocError('');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin', { replace: true });
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: TDS.color.bgPrimary }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `${TDS.spacing.lg}px ${TDS.spacing.xl}px`,
          borderBottom: `1px solid ${TDS.color.gray100}`,
          backgroundColor: TDS.color.white,
        }}
      >
        <h1 style={{ ...TDS.typography.h3, color: TDS.color.textPrimary }}>
          관리자 대시보드
        </h1>
        <button
          onClick={handleLogout}
          style={{
            ...TDS.typography.body3,
            color: TDS.color.textTertiary,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          로그아웃
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: `${TDS.spacing.xl}px ${TDS.spacing.xl}px` }}>
        <h2 style={{ ...TDS.typography.body1Bold, color: TDS.color.textPrimary, marginBottom: TDS.spacing.lg }}>
          승인 대기 중인 점주 ({owners.length})
        </h2>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <TdsSpinner size={32} />
          </div>
        )}

        {error && (
          <div
            style={{
              padding: `${TDS.spacing.md}px`,
              marginBottom: TDS.spacing.md,
              backgroundColor: '#FEF2F2',
              borderRadius: TDS.radius.md,
              color: TDS.color.red,
              ...TDS.typography.body3,
            }}
          >
            {error}
          </div>
        )}

        {!loading && owners.length === 0 && !error && (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: TDS.color.textTertiary,
              ...TDS.typography.body3,
            }}
          >
            대기 중인 점주가 없습니다
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {owners.map((owner) => (
            <div
              key={owner.id}
              style={{
                backgroundColor: TDS.color.white,
                borderRadius: TDS.radius.lg,
                padding: `${TDS.spacing.lg}px`,
                border: `1px solid ${TDS.color.gray100}`,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <p style={{ ...TDS.typography.body2Bold, color: TDS.color.textPrimary, marginBottom: 4 }}>
                  {owner.representativeName}
                </p>
                <p style={{ ...TDS.typography.caption1, color: TDS.color.textTertiary }}>
                  사업자번호: {owner.businessNumber}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {owner.email && (
                  <InfoItem label="이메일" value={owner.email} />
                )}
                <InfoItem label="연락처" value={owner.representativePhone} />
                {owner.naverPlaceLink && (
                  <InfoItem label="네이버지도" value={owner.naverPlaceLink} />
                )}
                <InfoItem label="신청일" value={new Date(owner.createdAt).toLocaleDateString('ko-KR')} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <TdsButton
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => void handleViewDocument(owner.id)}
                >
                  사업자등록증 보기
                </TdsButton>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TdsButton
                    variant="ghost"
                    size="sm"
                    fullWidth
                    loading={actionId === owner.id}
                    onClick={() => void handleReject(owner.id)}
                  >
                    거절
                  </TdsButton>
                  <TdsButton
                    variant="primary"
                    size="sm"
                    fullWidth
                    loading={actionId === owner.id}
                    onClick={() => void handleApprove(owner.id)}
                  >
                    승인
                  </TdsButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 사업자등록증 미리보기 모달 */}
      {docOwnerId !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="사업자등록증 미리보기"
          onClick={closeDocument}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: TDS.spacing.lg,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: TDS.color.white,
              borderRadius: TDS.radius.lg,
              padding: TDS.spacing.lg,
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              gap: TDS.spacing.md,
              minWidth: 280,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ ...TDS.typography.body1Bold, color: TDS.color.textPrimary }}>
                사업자등록증
              </h3>
              <button
                onClick={closeDocument}
                aria-label="닫기"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 20,
                  color: TDS.color.textTertiary,
                  padding: 4,
                }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                maxHeight: 'calc(90vh - 100px)',
                overflow: 'auto',
              }}
            >
              {docLoading && <TdsSpinner size={32} />}
              {docError && (
                <p style={{ ...TDS.typography.body3, color: TDS.color.red }}>
                  {docError}
                </p>
              )}
              {docUrl && (
                <img
                  src={docUrl}
                  alt="사업자등록증"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ ...TDS.typography.caption2, color: TDS.color.textTertiary, width: 60, flexShrink: 0 }}>
        {label}
      </span>
      <span
        style={{
          ...TDS.typography.caption1,
          color: TDS.color.textSecondary,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  );
}
